// App.js
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";

import Sidebar from "./components/layout/Sidebar";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { canViewModule, readSessionUser } from "./components/auth/moduleAccess";
import Navbar from "./components/layout/Navbar";
import NetworkProgress from "./components/layout/NetworkProgress";
import "./App.css";
import "./components/pages/adminWorkspace.css";

// Operational modules are intentionally deferred until their route is opened.
// This keeps sign-in and the first workspace load responsive even as individual
// CRM modules continue to grow independently.
const Login = lazy(() => import("./components/pages/login/Login"));
const Dashboard = lazy(() => import("./components/pages/dashboard/DashboardPage.js"));
const CallCenterInterface = lazy(() => import("./components/pages/callCenterInterface/CallCenterInterface"));
const VonHubInterface = lazy(() => import("./components/pages/vonHubInterface/VonHubInterface"));
const CustomerSearch = lazy(() => import("./components/pages/customerSearch/CustomerSearch"));
const CallsInfo = lazy(() => import("./components/pages/vonageCallsInfo/VonageCallsInfo"));
const CallCentreAudit = lazy(() => import("./components/pages/callCentreAudit/CallCentreAudit"));
const LocationsInfo = lazy(() => import("./components/pages/location/Location"));
const HubSpotInfo = lazy(() => import("./components/pages/hubSpotEmailsInfo/HubSpotEmailsInfo"));
const UsersSetup = lazy(() => import("./components/pages/usersSetup/UserSetup.js"));
const AccessSetup = lazy(() => import("./components/pages/accessSetup/AccessSetup.js"));
const AgentsSetup = lazy(() => import("./components/pages/agentsSetup/AgentsSetup.js"));
const UnifiedCRM = lazy(() => import("./components/pages/unifiedCrm/UnifiedCrm"));
const EmailTickets = lazy(() => import("./components/pages/emailTickets/EmailTickets"));
const TicketAudit = lazy(() => import("./components/pages/emailTickets/TicketAudit"));
const EmailSurveys = lazy(() => import("./components/pages/emailTickets/EmailSurveys"));
const LocationWorkspace = lazy(() => import("./components/pages/locationWorkspace/LocationWorkspace"));
const LocationOversight = lazy(() => import("./components/pages/locationWorkspace/LocationOversight"));
const SystemAudit = lazy(() => import("./components/pages/systemAudit/SystemAudit"));
const PublicEmailSurvey = lazy(() => import("./components/pages/emailTickets/PublicEmailSurveyV2"));
const HubSpotArchive = lazy(() => import("./components/pages/hubSpotArchive/HubSpotArchive"));
const CatalogSetup = lazy(() => import("./components/pages/catalogSetup/CatalogSetup"));
const CustomerInfo360 = lazy(() => import("./components/pages/customerInfo360/CustomerInfo360"));

// Deliberately based on real user interaction, never background polling.  A
// dashboard refresh must not keep an unattended CRM session alive.
const IDLE_LOGOUT_MS = 4 * 60 * 60 * 1000;
const LAST_USER_ACTIVITY_KEY = "ringoi_last_user_activity_at";

function safeJsonParse(raw, fallback = null) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (err) {
    console.error("Invalid JSON in storage:", err);
    return fallback;
  }
}

function getLoggedUser() {
  const user = safeJsonParse(sessionStorage.getItem("user_token"), null);

  if (!user || typeof user !== "object") return null;

  // Required user fields check. Some login payloads use userEmail, some use email.
  if (!user.role && !user.userEmail && !user.email) return null;

  return user;
}

function isLocationWorkspaceRole(user) {
  const role = String(user?.role || "").replace(/[\s_-]+/g, "").toLowerCase();
  return role.includes("locationmanager") || role.includes("locationadmin");
}

function isUserAuthenticated() {
  const auth = localStorage.getItem("auth") === "true";
  const user = getLoggedUser();

  return auth && Boolean(user);
}

function normalizeTheme(value) {
  if (value === true) return "dark";
  const raw = String(value || "").toLowerCase();

  if (raw === "dark" || raw === "dark-mode" || raw.includes("dark")) return "dark";
  return "light";
}

function getInitialTheme() {
  try {
    const stored = localStorage.getItem("crm_theme") || localStorage.getItem("td_theme");
    if (stored) return normalizeTheme(stored);

    if (window.matchMedia?.("(prefers-color-scheme: dark)")?.matches) {
      return "dark";
    }
  } catch {}

  return "light";
}

function applyThemeToDocument(theme) {
  const normalized = normalizeTheme(theme);

  try {
    document.documentElement.setAttribute("data-theme", normalized);
    document.body?.setAttribute("data-theme", normalized);
    localStorage.setItem("crm_theme", normalized);
    localStorage.setItem("td_theme", normalized);
  } catch {}
}

function WorkspaceRouteLoading() {
  return (
    <section className="app-route-loading" role="status" aria-live="polite" aria-label="Loading workspace">
      <span className="app-route-loading__spinner" aria-hidden="true" />
      <div>
        <strong>Loading workspace</strong>
        <p>Preparing the tools and data for this module…</p>
      </div>
    </section>
  );
}

function ModuleRoute({ moduleName, children }) {
  const user = readSessionUser();
  if (canViewModule(user, moduleName)) return children;

  return (
    <section
      role="alert"
      style={{
        maxWidth: 680,
        margin: "52px auto",
        padding: 28,
        border: "1px solid #fecaca",
        borderRadius: 16,
        background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
        color: "#334155",
      }}
    >
      <p style={{ margin: 0, color: "#b91c1c", fontWeight: 800, fontSize: 18 }}>
        Module access is not enabled
      </p>
      <p style={{ margin: "10px 0 0", lineHeight: 1.6 }}>
        Your role does not currently have View access for <strong>{moduleName}</strong>. Ask an authorised administrator to update the role in Access Setup, then sign in again.
      </p>
    </section>
  );
}

function AppShell() {
  const [collapsedFlag, setCollapsedFlag] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(isUserAuthenticated());
  const [theme, setTheme] = useState(getInitialTheme);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    const validLogin = isUserAuthenticated();

    if (!validLogin) {
      localStorage.removeItem("auth");
      setIsLoggedIn(false);

      if (location.pathname !== "/") {
        navigate("/", { replace: true });
      }
    } else {
      setIsLoggedIn(true);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === "auth" || event.key === "crm_theme" || event.key === "td_theme") {
        if (event.key === "auth") {
          setIsLoggedIn(isUserAuthenticated());
        }

        if (event.key === "crm_theme" || event.key === "td_theme") {
          setTheme(getInitialTheme());
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleToggleTheme = useCallback((nextTheme) => {
    setTheme((current) => {
      if (typeof nextTheme === "string" || typeof nextTheme === "boolean") {
        return normalizeTheme(nextTheme);
      }

      return current === "dark" ? "light" : "dark";
    });
  }, []);

  const handleLoginSuccess = useCallback(() => {
    const user = getLoggedUser();

    if (!user) {
      console.warn("Login success called, but user_token is missing");
      localStorage.removeItem("auth");
      setIsLoggedIn(false);
      navigate("/", { replace: true });
      return;
    }

    localStorage.setItem("auth", "true");
    localStorage.setItem(LAST_USER_ACTIVITY_KEY, String(Date.now()));
    setIsLoggedIn(true);

    navigate(
      isLocationWorkspaceRole(user) ? "/LocationWorkspace" : "/Dashboard",
      { replace: true },
    );
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let closed = false;
    let lastWrittenAt = 0;
    const clearSession = () => {
      if (closed) return;
      closed = true;
      localStorage.removeItem("auth");
      localStorage.removeItem(LAST_USER_ACTIVITY_KEY);
      sessionStorage.removeItem("user_token");
      setIsLoggedIn(false);
      navigate("/", { replace: true, state: { reason: "idle_timeout" } });
    };
    const noteActivity = () => {
      const now = Date.now();
      // Writing at most once per minute keeps cross-tab session coordination
      // cheap while retaining a precise four-hour inactivity boundary.
      if (now - lastWrittenAt >= 60000) {
        lastWrittenAt = now;
        localStorage.setItem(LAST_USER_ACTIVITY_KEY, String(now));
        // The server accepts this only with the signed current-session token.
        // Background requests never call it, so it represents real activity.
        try {
          const user = safeJsonParse(sessionStorage.getItem("user_token"), {});
          const token = String(user?.accessToken || "").trim();
          if (token) {
            fetch(`${process.env.REACT_APP_API_BASE_URL || "https://api.ringoi.com"}/auth/session/activity`, {
              method: "POST", headers: { Authorization: `Bearer ${token}` }, credentials: "include",
            }).catch(() => {});
          }
        } catch {}
      }
    };
    const checkIdle = () => {
      const last = Number(localStorage.getItem(LAST_USER_ACTIVITY_KEY) || 0);
      if (!last || Date.now() - last >= IDLE_LOGOUT_MS) clearSession();
    };
    noteActivity();
    const activityEvents = ["pointerdown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, noteActivity, { passive: true }));
    const onVisibility = () => { if (document.visibilityState === "visible") checkIdle(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", checkIdle);
    const interval = window.setInterval(checkIdle, 60000);
    return () => {
      closed = true;
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, noteActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", checkIdle);
      window.clearInterval(interval);
    };
  }, [isLoggedIn, navigate]);

  const routes = useMemo(
    () => [
      { path: "/Dashboard", moduleName: "Dashboard", element: <Dashboard /> },
      { path: "/CallCenterInterface", moduleName: "Call Center Interface", element: <CallCenterInterface /> },
      { path: "/Von-HubInterface", moduleName: "Von-Hub Interface", element: <VonHubInterface /> },
      { path: "/CustomerSearch", moduleName: "Customer Search", element: <CustomerSearch /> },
      { path: "/CallsInfo", moduleName: "Calls Info", element: <CallsInfo /> },
      { path: "/CallCentreAudit", moduleName: "Call Centre Audit", element: <CallCentreAudit /> },
      { path: "/LocationsInfo", moduleName: "Locations Info", element: <LocationsInfo /> },
      { path: "/HubSpotInfo", moduleName: "HubSpot Info", element: <HubSpotInfo /> },
      { path: "/UsersSetup", moduleName: "Users Setup", element: <UsersSetup /> },
      { path: "/AccessSetup", moduleName: "Access Setup", element: <AccessSetup /> },
      { path: "/AgentsSetup", moduleName: "Agents Setup", element: <AgentsSetup /> },
      { path: "/CatalogSetup", moduleName: "Catalog Setup", element: <CatalogSetup /> },
      { path: "/CustomerInfo360", moduleName: "Customer Info 360", element: <CustomerInfo360 /> },
      { path: "/UnifiedCRM", moduleName: "Unified CRM", element: <UnifiedCRM /> },
      { path: "/EmailTickets", moduleName: "Email Tickets", element: <EmailTickets /> },
      { path: "/EmailTicketAudit", moduleName: "Email Ticket Audit", element: <TicketAudit /> },
      // Sidebar routes module names by removing spaces, so "Email Survey"
      // resolves to /EmailSurvey. Keep the plural path as a harmless direct
      // link alias for anyone who has already bookmarked it.
      { path: "/EmailSurvey", moduleName: "Email Survey", element: <EmailSurveys /> },
      { path: "/EmailSurveys", moduleName: "Email Survey", element: <EmailSurveys /> },
      { path: "/LocationWorkspace", moduleName: "Location Workspace", element: <LocationWorkspace /> },
      { path: "/LocationOversight", moduleName: "Location Oversight", element: <LocationOversight /> },
      { path: "/SystemAudit", moduleName: "System Audit", element: <SystemAudit /> },
      { path: "/HubSpotArchive", moduleName: "HubSpot Archive", element: <HubSpotArchive /> },
    ],
    []
  );

  const isLoginPage = location.pathname === "/";
  // A customer follows an unguessable, expiring token from their survey email.
  // It must be reachable without a CRM login or the shared customer feedback
  // flow would redirect them to the internal sign-in screen.
  const publicSurveyPath = location.pathname.startsWith("/email-survey/")
    ? location.pathname
    : (window.location.hash || "").replace(/^#/, "");
  if (publicSurveyPath.startsWith("/email-survey/")) {
    return (
      <Suspense fallback={<WorkspaceRouteLoading />}>
        <PublicEmailSurvey surveyPath={publicSurveyPath} />
      </Suspense>
    );
  }

  if (isLoginPage) {
    return isLoggedIn ? (
      <Navigate
        to={isLocationWorkspaceRole(getLoggedUser()) ? "/LocationWorkspace" : "/Dashboard"}
        replace
      />
    ) : (
      <Suspense fallback={<WorkspaceRouteLoading />}>
        <Login onLogin={handleLoginSuccess} />
      </Suspense>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Navbar theme={theme} onToggleTheme={handleToggleTheme} />
      <NetworkProgress />

      <div className={`customSidebar app-theme-${theme}`}>
        <Sidebar
          setCollapsedFlag={setCollapsedFlag}
          theme={theme}
        />

        <main
          className={collapsedFlag ? "rightSideContentCollasped" : "rightSideContent"}
          data-sidebar-collapsed={collapsedFlag ? "true" : "false"}
        >
          <div className="content-shell">
            <Suspense fallback={<WorkspaceRouteLoading />}>
              <Routes>
                <Route path="/" element={<Navigate to="/Dashboard" replace />} />

                {routes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <ProtectedRoute>
                        <ModuleRoute moduleName={route.moduleName}>
                          {route.element}
                        </ModuleRoute>
                      </ProtectedRoute>
                    }
                  />
                ))}

                <Route path="*" element={<Navigate to="/Dashboard" replace />} />
              </Routes>
            </Suspense>
          </div>
        </main>
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}
